import { nodeDefs } from "src/nodes";
import { arrayNodeDef } from "src/nodes/fako";
import { ChildrenGenerator } from "src/types/generator.types";
import { NodeContext } from "src/types/node-context.types";
import { NestedTreeNode } from "src/types/tree.types";
import { NodeContextImpl } from "./context.helpers";

export async function generateNodes(
  nestedTreeNodes: NestedTreeNode[]
): Promise<unknown> {
  if (nestedTreeNodes.length === 0) return "";

  const { rootTreeNode, wrapped } = (() => {
    if (nestedTreeNodes.length === 1) {
      return {
        rootTreeNode: nestedTreeNodes[0],
        wrapped: false,
      };
    }

    const rootNode = arrayNodeDef.create("root", { length: 1 });
    const rootTreeNode: NestedTreeNode = {
      node: rootNode,
      isActive: true,
      children: nestedTreeNodes,
    };

    return { rootTreeNode, wrapped: true };
  })();

  const result = await generateSingleNode(rootTreeNode, new NodeContextImpl());

  if (wrapped && Array.isArray(result) && result.length <= 1) {
    // Unwrap created array if it's a single element
    return result[0];
  }

  return result;
}

async function generateSingleNode(
  nestedTreeNode: NestedTreeNode,
  context: NodeContext
): Promise<unknown> {
  const nodeDef = nodeDefs[nestedTreeNode.node.type];
  const children = nodeDef.acceptsChildren ? nestedTreeNode.children : [];
  const generate = (node: NestedTreeNode, childContext?: NodeContext) => {
    return generateSingleNode(node, childContext || context);
  };

  function buildChildrenGenerator(
    childContext = context.copy()
  ): ChildrenGenerator {
    async function* loop(ctxProvider?: (ctx: NodeContext) => NodeContext) {
      for (const childTreeNode of children) {
        ctxProvider && (childContext = ctxProvider(childContext));
        const value = await generate(childTreeNode, childContext);

        if (value !== undefined) {
          childContext.append({
            name: childTreeNode.node.name,
            nodeType: childTreeNode.node.type,
            value,
          });
          yield { value, childTreeNode };
        }
      }
    }

    return {
      getAll: () => children,
      getContext: () => childContext,
      withContext: (newCtx: NodeContext) => buildChildrenGenerator(newCtx),
      generateValues: async () => {
        const results = [];
        for await (const { value } of loop()) {
          results.push(value);
        }
        return results;
      },
      loop,
    };
  }

  return await nodeDef.generate({
    node: nestedTreeNode.node,
    children: buildChildrenGenerator(),
    context,
  });
}
